// The TASK-DETAIL MODAL (D, N-BOARD-1 task detail) — one Task thread, recreated 1:1 from the prototype's
// modal anatomy (app.css .scrim/.modal/.m-top/.m-head/.brief/.m-mid/.m-rail/.m-foot): the state pill row,
// the Spectral title + owner line, the collapsible Brief & approach grid, the two-column middle (the
// complete shared conversation + the "Message Ritona on this task" composer on the left; the Now rail on
// the right), and the append-only footer. It reads the Task-detail projection (brief/approach, every run in
// the one enduring Task Session, its plan/activity, the live agent-working state + next wake/escalation) and
// renders it natively.
//
// The composer routes the human's message SCOPED TO THE TASK — a thin transport carrying ONLY the Task id in
// the path + the typed message in the body. The server resolves the canonical Session; the browser cannot
// mint, select, or send one. Tokens only (G-181); every displayed state derives from the owning lifecycle
// (G-182) — the dashboard writes no Task state.
//
// The permanent-delete control lives HERE, in the modal footer (owner ruling 2026-07-24: the board card
// stays a pure Signal; deleting is a considered act taken from the thread, two-step confirmed, never a
// card affordance). The owning Tasks Gate still re-checks human authority on the route.

import { useCallback, useEffect, useState } from 'react';
import { Icon } from './Icon.tsx';
import { useSurfaceApi } from '../surfaces/SurfaceApiContext.tsx';
import { useProjection, type ProjectionState } from '../surfaces/useProjection.ts';
import { useTaskThread, type ComposerStash } from '../surfaces/useTaskThread.ts';
import { SurfaceFrame } from '../surfaces/surface-frame.tsx';
import { TaskThreadConversation } from './TaskThreadConversation.tsx';
import { TaskReviewPanel } from './TaskReviewPanel.tsx';
import { TaskDeleteControl } from './TaskDeleteControl.tsx';
import { TaskModalTop, TASK_LIVE_LABEL } from './TaskModalTop.tsx';
import { useTaskThreadCarryover } from './useTaskThreadCarryover.ts';
import type { TaskDetailProjection, TaskLiveState } from '../surfaces/know-projections.ts';
import type {
  TaskComposerRetention,
  TaskComposerRetentionEvent,
} from '../surfaces/task-request-identity.ts';

/** The Now-card tone class per live state (prototype .now / warn / draft). */
const LIVE_NOW_CLASS: Readonly<Record<TaskLiveState, string>> = {
  agent_working: 'now',
  waiting: 'now warn',
  idle: 'now draft',
};

/** The prototype's entrance: mount, then add `on` next frame so the transition runs (reduced-motion safe). */
function useEntranceTransition(): boolean {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return entered;
}

/** Modal display state: the entrance transition + the v2 hide-chat layout toggle. Display-only — held
 *  above SurfaceFrame so a projection reload keeps the reader's chosen layout. */
function useModalDisplay(): {
  readonly entered: boolean;
  readonly chatHidden: boolean;
  readonly toggleChat: () => void;
} {
  const entered = useEntranceTransition();
  const [chatHidden, setChatHidden] = useState(false);
  const toggleChat = useCallback(() => setChatHidden((current) => !current), []);
  return { entered, chatHidden, toggleChat };
}

interface TaskModalContentsProps {
  readonly taskId: string;
  readonly state: ProjectionState<TaskDetailProjection>;
  readonly chatHidden: boolean;
  readonly toggleChat: () => void;
  readonly taskActionNotice: string | null;
  readonly setTaskActionNotice: (notice: string) => void;
  readonly streamFailureFence: React.MutableRefObject<string | null>;
  readonly composerStash: React.MutableRefObject<ComposerStash>;
  readonly retention: TaskComposerRetention | null;
  readonly onRetentionEvent: (event: TaskComposerRetentionEvent) => boolean;
  readonly onClose: () => void;
}

function TaskModalContents({
  taskId,
  state,
  chatHidden,
  toggleChat,
  taskActionNotice,
  setTaskActionNotice,
  streamFailureFence,
  composerStash,
  retention,
  onRetentionEvent,
  onClose,
}: TaskModalContentsProps): React.JSX.Element {
  return (
    <>
      <TaskModalTop
        detail={state.data}
        chatHidden={chatHidden}
        onToggleChat={toggleChat}
        onClose={onClose}
      />
      {taskActionNotice !== null ? (
        <p role="status" className="gate-note task-review-note">
          {taskActionNotice}
        </p>
      ) : null}
      <SurfaceFrame state={state}>
        {(detail) => (
          <TaskThread
            taskId={taskId}
            detail={detail}
            reload={state.reload}
            streamFailureFence={streamFailureFence}
            composerStash={composerStash}
            retention={retention}
            onRetentionEvent={onRetentionEvent}
            onTaskActionNotice={setTaskActionNotice}
            onClose={onClose}
          />
        )}
      </SurfaceFrame>
    </>
  );
}

/** The task-detail modal — reads the Task thread projection and renders it inside the dialog frame. */
export function TaskDetailModal({
  taskId,
  retention,
  onRetentionEvent,
  onClose,
}: {
  readonly taskId: string;
  readonly retention: TaskComposerRetention | null;
  readonly onRetentionEvent: (event: TaskComposerRetentionEvent) => boolean;
  readonly onClose: () => void;
}): React.JSX.Element {
  const api = useSurfaceApi();
  const load = useCallback(() => api.getTaskDetail(taskId), [api, taskId]);
  const state = useProjection<TaskDetailProjection>(load);
  const { streamFailureFence, composerStash } = useTaskThreadCarryover(retention);
  const { entered, chatHidden, toggleChat } = useModalDisplay();
  const [taskActionNotice, setTaskActionNotice] = useState<string | null>(null);
  useEffect(() => setTaskActionNotice(null), [taskId]);

  return (
    <div
      className={entered ? 'scrim on' : 'scrim'}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Task detail"
        className={chatHidden ? 'modal chat-hidden' : 'modal'}
        data-task-modal={taskId}
      >
        <TaskModalContents
          taskId={taskId}
          state={state}
          chatHidden={chatHidden}
          toggleChat={toggleChat}
          taskActionNotice={taskActionNotice}
          setTaskActionNotice={setTaskActionNotice}
          streamFailureFence={streamFailureFence}
          composerStash={composerStash}
          retention={retention}
          onRetentionEvent={onRetentionEvent}
          onClose={onClose}
        />
      </div>
    </div>
  );
}

/** The Task thread body — the prototype modal anatomy over the real projection, laid straight into the
 *  .modal flex column so the feed and rail keep their own internal scroll (prototype .m-mid geometry). */
interface TaskThreadProps {
  readonly taskId: string;
  readonly detail: TaskDetailProjection;
  readonly reload: () => void;
  readonly streamFailureFence: React.MutableRefObject<string | null>;
  readonly composerStash: React.MutableRefObject<ComposerStash>;
  readonly retention: TaskComposerRetention | null;
  readonly onRetentionEvent: (event: TaskComposerRetentionEvent) => boolean;
  readonly onTaskActionNotice: (notice: string) => void;
  readonly onClose: () => void;
}

function TaskThread(props: TaskThreadProps): React.JSX.Element {
  const { taskId, detail, reload, onTaskActionNotice, onClose } = props;
  const thread = useTaskThread(props);
  return (
    <>
      <div className="m-head">
        <h2>{detail.title}</h2>
        <div className="m-who">
          <span>
            <span className="k">Owner</span>
            <b>{detail.ownerDisplayName}</b>
          </span>
          <span>
            <span className="k">Reviewer</span>
            <b>{detail.draftReviewerDisplayName}</b>
          </span>
        </div>
      </div>

      <Brief detail={detail} />

      <div className="m-mid">
        <TaskThreadConversation detail={detail} thread={thread} />
        <div className="m-rail">
          <div>
            <div className="m-rk">Now</div>
            <NowCard detail={detail} />
          </div>
          <DefinitionPlan detail={detail} />
          <TaskReviewPanel detail={detail} reload={reload} onActionNotice={onTaskActionNotice} />
        </div>
      </div>

      <TaskThreadFooter taskId={taskId} title={detail.title} onDeleted={onClose} />
    </>
  );
}

/** The modal footer: the permanent-delete control (deletion here is explicit and two-step confirmed —
 *  never silent). The two standing jargon hints it used to carry were dropped in the 29 Sep demo polish. */
function TaskThreadFooter({
  taskId,
  title,
  onDeleted,
}: {
  readonly taskId: string;
  readonly title: string;
  readonly onDeleted: () => void;
}): React.JSX.Element {
  return (
    <div className="m-foot">
      <div className="m-foot-right">
        <TaskDeleteControl taskId={taskId} title={title} onDeleted={onDeleted} />
      </div>
    </div>
  );
}

/** The definition plan as the rail checklist (v2 Signal): done fills, the current step rings, waiting
 *  steps stay quiet, a blocked step flags itself — status straight off the projection (G-182). */
function DefinitionPlan({ detail }: { readonly detail: TaskDetailProjection }): React.JSX.Element {
  return (
    <div>
      <div className="m-rk">Plan · revision {detail.definitionRevision}</div>
      {detail.plan.length > 0 ? (
        <ol className="m-plan" aria-label="Definition plan">
          {detail.plan.map((step, index) => (
            <li key={`${step.id}-${String(index)}`} data-step-status={step.status}>
              <span className="ck" aria-hidden="true">
                {step.status === 'completed' ? <Icon name="check" size={9} /> : null}
              </span>
              <span className="m-plan-text">{step.text}</span>
              {step.status === 'blocked' ? <span className="m-plan-flag">blocked</span> : null}
              {/* WHO acts, mirroring the owner's brief email — a step the owner must perform reads as
                  theirs here too, so the board and the brief never disagree about the hand-offs. */}
              <span className="m-plan-actor">{step.actor === 'owner' ? 'Owner' : 'Ritona'}</span>
            </li>
          ))}
        </ol>
      ) : (
        <div className="m-plan-empty">No plan has been added yet.</div>
      )}
    </div>
  );
}

function ReviewContext({ detail }: { readonly detail: TaskDetailProjection }): React.JSX.Element {
  return (
    <>
      {detail.reviewFeedback !== null ? (
        <div className="task-review-feedback">
          <div className="bk">Changes requested</div>
          <div className="bv">{detail.reviewFeedback}</div>
        </div>
      ) : null}
      {detail.deferredUntil !== null ? (
        <div className="task-deferred-until">
          <div className="bk">Deferred until</div>
          <div className="bv">{detail.deferredUntil}</div>
        </div>
      ) : null}
      {detail.outcomeNote !== null ? (
        <div style={{ gridColumn: '1 / -1' }}>
          <div className="bk">{terminalReasonLabel(detail.closureKind)}</div>
          <div className="bv">{detail.outcomeNote}</div>
        </div>
      ) : null}
    </>
  );
}

/** Use the Tasks-owned terminal disposition; state=dropped alone cannot distinguish four decisions. */
function terminalReasonLabel(kind: TaskDetailProjection['closureKind']): string {
  switch (kind) {
    case 'completed':
      return 'Outcome';
    case 'gate1_rejected':
    case 'gate2_rejected':
      return 'Rejection reason';
    case 'cancelled':
      return 'Cancellation reason';
    case 'unresolved':
      // The stop that is NOT done: the goal was never reached, so the note says what is still outstanding.
      return 'Stopped unresolved — still outstanding';
    case 'dropped':
      return 'Drop reason';
    case null:
      return 'Reason';
  }
}

/** The collapsed-by-default brief (v2 Signal: the closed header carries its one-line summary; the plan
 *  lives in the rail, not here). */
function Brief({ detail }: { readonly detail: TaskDetailProjection }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const summary = detail.brief ?? 'Not yet briefed.';
  return (
    <section aria-label="Task brief and approach" className={open ? 'brief open' : 'brief'}>
      <button
        type="button"
        className="bh gate-open"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        style={{ display: 'flex', width: '100%' }}
      >
        <span className="k">Brief &amp; approach</span>
        <span className="pk">{detail.managerSummary || summary}</span>
        <span className="cv" aria-hidden="true">
          <Icon name="chevdown" size={12} />
        </span>
      </button>
      <div className="bd">
        <div className="bgrid">
          {/* The GOAL leads, as it does on the owner's brief email — the owner approves here against the
              same statement of what done looks like that they read there. Rendered only when one is
              stated; an empty Goal row would imply a goal the definition does not carry. */}
          {detail.goal !== null ? (
            <div style={{ gridColumn: '1 / -1' }}>
              <div className="bk">Goal</div>
              <div className="bv">{detail.goal}</div>
            </div>
          ) : null}
          <div>
            <div className="bk">Brief</div>
            <div className="bv">{summary}</div>
          </div>
          <div>
            <div className="bk">Approach</div>
            <div className="bv">{detail.approach ?? 'Not yet set.'}</div>
          </div>
          {detail.managerSummary.length > 0 ? (
            <div style={{ gridColumn: '1 / -1' }}>
              <div className="bk">Manager summary</div>
              <div className="bv">{detail.managerSummary}</div>
            </div>
          ) : null}
          <ReviewContext detail={detail} />
        </div>
      </div>
    </section>
  );
}

/** The Now card in the right rail (prototype .now) — live state + parked reason + next wake. */
function NowCard({ detail }: { readonly detail: TaskDetailProjection }): React.JSX.Element {
  return (
    <div className={LIVE_NOW_CLASS[detail.liveState]} data-live-state={detail.liveState}>
      <div className="nh">
        <span className="dot" aria-hidden="true" /> {TASK_LIVE_LABEL[detail.liveState]}
      </div>
      <div className="nb">
        {detail.parkedReason !== null ? <>{detail.parkedReason} </> : null}
        {detail.parkedReason === null && detail.wakeReason !== null ? (
          <>{detail.wakeReason} </>
        ) : null}
        {detail.nextWakeAt !== null ? <>Next check · {detail.nextWakeAt}.</> : null}
        {detail.parkedReason === null && detail.nextWakeAt === null
          ? detail.waitingOn === 'draft_reviewer'
            ? 'Waiting for the draft reviewer before the owner is contacted.'
            : detail.waitingOn === 'owner'
              ? 'Waiting for the owner before Ritona starts work.'
              : 'Nothing is awaited from you on this thread right now.'
          : null}
      </div>
    </div>
  );
}
